import { createContext, useContext, useEffect, type ReactNode } from 'react';
import type { ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import { getTokenValue } from 'tamagui';

/**
 * PSSkeleton — a loading-placeholder primitive (atom).
 *
 * The idea (borrowed from react-native-reanimated-skeleton, minus its bare
 * `react-native-linear-gradient` dependency): the skeleton's shape is defined by
 * the LAYOUT you place its bones in, not by a config array. You lay out
 * `<PSSkeleton.Bone/>`s exactly where the real content will sit — each bone owns
 * its own dimensions via ordinary style props — and they pulse in unison. A bone
 * whose height is derived from its own width (the 3:4 hero image) works because
 * it's just a View: give it `aspectRatio`, same as the real component would.
 *
 * We build this on `react-native-reanimated` (already a dependency) rather than
 * pull in the skeleton lib because that lib hard-imports a bare RN gradient
 * module for its shimmer, which doesn't belong in this managed Expo app. So the
 * animation is a PULSE (opacity in/out) — no gradient, no shimmer sweep — which
 * is the animation the lib itself falls back to without the gradient.
 *
 * A bone is a reanimated `Animated.View` styled with a plain RN style, NOT a
 * Tamagui component: the tokens it needs are resolved via
 * `getTokenValue`, and RN's `DimensionValue` accepts the percentage widths
 * ("80%") that Tamagui's animated-component prop types reject. So bones take RN
 * style props (`width`, `height`, `aspectRatio`, `borderRadius`), not `$token`
 * shorthands.
 *
 * One pulse clock lives at the `<PSSkeleton>` root and is shared to every bone
 * via context, so a screenful of bones breathes together instead of each running
 * its own drifting timer.
 *
 * Usage:
 *   <PSSkeleton>
 *     <PSSkeleton.Bone style={{ height: 24, width: 180 }} />
 *     <PSSkeleton.Bone style={{ width: '100%', aspectRatio: 3 / 4 }} />
 *   </PSSkeleton>
 */

// Opacity endpoints for the pulse and how long one half-cycle takes. The bone
// color is a token ($backgroundSecondary); the pulse is purely opacity so it
// reads as "breathing" without a second color.
const PULSE_MIN = 0.4;
const PULSE_MAX = 1;
const PULSE_MS = 800;

// The shared pulse opacity. Bones read this off context so they all animate from
// the SAME clock (one repeating timeline, not one per bone).
const PulseContext = createContext<SharedValue<number> | null>(null);

/**
 * The skeleton root: owns the single pulse clock and hands it down. It's a
 * context provider only — the caller wraps the bones in their own layout
 * container (YStack with gap, etc.); the bones inside do the animating.
 */
function PSSkeleton({ children }: { children: ReactNode }) {
  const pulse = useSharedValue(PULSE_MAX);

  useEffect(() => {
    // Ping-pong between the two opacities forever. `withSequence` gives the
    // down-then-up so it eases both ways; `withRepeat(-1)` loops indefinitely.
    pulse.value = withRepeat(
      withSequence(
        withTiming(PULSE_MIN, { duration: PULSE_MS }),
        withTiming(PULSE_MAX, { duration: PULSE_MS })
      ),
      -1
    );
  }, [pulse]);

  return (
    <PulseContext.Provider value={pulse}>{children}</PulseContext.Provider>
  );
}

/**
 * A single bone. Give it a `style` with the layout it should occupy (`width`,
 * `height`, `aspectRatio`, `borderRadius`, `flex`, …) and it pulses its opacity
 * off the shared clock. The bone color + a default rounded corner are baked in;
 * `style` can override either. Must be rendered inside a `<PSSkeleton>` (it reads
 * the pulse from context).
 */
function Bone({ style }: { style?: ViewStyle }) {
  const pulse = useContext(PulseContext);
  if (!pulse) {
    throw new Error('PSSkeleton.Bone must be rendered inside <PSSkeleton>.');
  }

  // The bone's neutral surface color + corner (not Tamagui props — this is a
  // plain RN-styled Animated.View; see the file header). Dotted-path form,
  // matching the `getTokenValue('$color.background')` idiom used for raw native
  // styles elsewhere (see app/_layout.tsx).
  //
  // Resolved HERE, in render — never at module scope. `getTokenValue` reads the
  // global Tamagui config, which doesn't exist until `createTamagui()` has run
  // (tamagui.config.ts, imported by app/_layout.tsx). At module scope these run
  // at IMPORT time, and the web static-render pass pulls in route modules
  // (app/index.tsx → HomeSkeleton → here) before that layout import — so they
  // read a null config and crashed the whole web build. Plain object lookups,
  // so no memo needed.
  const boneColor = getTokenValue('$color.backgroundSecondary');
  const boneRadius = getTokenValue('$radius.base');

  const animatedStyle = useAnimatedStyle(() => ({ opacity: pulse.value }));

  return (
    <Animated.View
      style={[
        { backgroundColor: boneColor, borderRadius: boneRadius },
        style,
        animatedStyle,
      ]}
    />
  );
}

PSSkeleton.Bone = Bone;

export default PSSkeleton;
